import { createAction } from 'redux-actions';
// eslint-disable-next-line import/no-cycle
import { IPunchInRecord } from '@src/services/punchIn';
import {
	GET_PUNCH_IN_CALL,
	GET_PUNCH_IN_DONE,
	GET_PUNCH_IN_FAIL,
	POST_PUNCH_IN_RECORD_CALL,
	POST_PUNCH_IN_RECORD_DONE,
	POST_PUNCH_IN_RECORD_FAIL,
	POST_PUNCH_IN_RECORD_QUEUE_CALL,
	POST_PUNCH_IN_RECORD_QUEUE_DONE,
	POST_PUNCH_IN_RECORD_QUEUE_FAIL,
	GET_DAY_SHIFTS_CALL,
	GET_DAY_SHIFTS_DONE,
	GET_DAY_SHIFTS_FAIL,
	GET_ALL_SHIFTS_CALL,
	GET_ALL_SHIFTS_DONE,
	GET_ALL_SHIFTS_FAIL,
	GET_SUITABLE_SHIFTS_CALL,
	GET_SUITABLE_SHIFTS_DONE,
	GET_SUITABLE_SHIFTS_FAIL,
	GET_SCHEDULE_SHIFTS_CALL,
	GET_SCHEDULE_SHIFTS_DONE,
	GET_SCHEDULE_SHIFTS_FAIL,
	GET_DAILY_SHIFTS_CALL,
	GET_DAILY_SHIFTS_DONE,
	GET_DAILY_SHIFTS_FAIL,
	GET_REST_PUNCH_RECORD_CALL,
	GET_REST_PUNCH_RECORD_DONE,
	GET_REST_PUNCH_RECORD_FAIL,
	CLEAR_REST_PUNCH_RECORD,
	POST_REST_PUNCH_RECORD_CALL,
	POST_REST_PUNCH_RECORD_DONE,
	POST_REST_PUNCH_RECORD_FAIL,
	DELETE_REST_PUNCH_RECORD_CALL,
	DELETE_REST_PUNCH_RECORD_DONE,
	DELETE_REST_PUNCH_RECORD_FAIL,
	GET_PUNCH_IN_RULE_CALL,
	GET_PUNCH_IN_RULE_DONE,
	GET_PUNCH_IN_RULE_FAIL,
	GET_PUNCH_IN_SHIFT_CALL,
	GET_PUNCH_IN_SHIFT_DONE,
	GET_PUNCH_IN_SHIFT_FAIL,
	GET_PUNCH_IN_SHIFTS_BY_RANGE_CALL,
	GET_PUNCH_IN_SHIFTS_BY_RANGE_DONE,
	GET_PUNCH_IN_SHIFTS_BY_RANGE_FAIL,
	SET_PUNCH_IN_STALE,
	PUSH_PUNCH_IN_RETRY_OUTCOME,
	CLEAR_PUNCH_IN_RETRY_OUTCOME,
	SET_ASYNC_PUNCH_ERROR_MODAL,
	CLEAR_ASYNC_PUNCH_ERROR_MODAL,
	SET_ASYNC_PUNCH_INTERACTION_LOCK,
	CLEAR_ASYNC_PUNCH_INTERACTION_LOCK,
	SET_MACHINE_PUNCH_ERROR,
} from '../types/punchIn';

export interface PunchRolloverSnapshot {
	timezone?: string;
	isRolloverPreviousDay: boolean;
	dailyRolloverHour: number;
	dailyRolloverMinute: number;
}

export interface PunchRetryContext {
	accountId: string;
	payload: IPunchInRecord;
	queueMeta: {
		displayLocationName?: string;
		displayShiftName?: string;
		allowPunchInOutOfRange?: boolean;
		rolloverSnapshot?: PunchRolloverSnapshot;
	};
}

export interface AsyncPunchInteractionLock {
	accountId: string;
	// UI 的 shift/duty key，供 durable IDB record 接手 bridge。
	key: string;
	// 單次 request owner（預產生的 queue record id）；terminal outcome 與舊 callback 只能清自己的 lock。
	ownerId: string;
	expiresAt: number;
}

export interface PunchInRetryOutcome {
	// queue record id
	id: string;
	// outcome 可能在離開 /hr 後才回來；consumer 必須用帳號隔離，避免切帳後顯示上一位使用者的結果。
	accountId: string;
	success: boolean;
	punchKind?: 'machine';
	machineResult?: import('@src/services/punchIn').TimeClockPunchResult;
	// machine 紀錄（punchKind: 'machine'）的 outcome 不帶 payload，consumer 一律 optional chaining
	payload?: IPunchInRecord;
	response?: {
		status?: number;
		data?: {
			raw?: string;
			error_type?: string;
		}
	};
	allowModal?: boolean;
	asyncError?: {
		code?: string;
		message?: string;
		// 直接 HTTP 4xx 的 response.data.error_type（例如裝置異常 'other_user_device'）
		errorType?: string;
	};
	// 前景失敗要能在 HRHome remount 後安全重打；不可再從當下 local state 猜 duty / shift / setting。
	retryContext?: PunchRetryContext;
}

export interface AsyncPunchError {
	id: string;
	// 後端 error_message，比對 whitelist（如 'ip is out of the ip range'）
	message?: string;
	errorType?: string;
	retryContext: PunchRetryContext;
}

export const getPunchInCall = createAction(GET_PUNCH_IN_CALL);
export const getPunchInDone = createAction(GET_PUNCH_IN_DONE);
export const getPunchInFail = createAction(GET_PUNCH_IN_FAIL);
export const postPunchInRecordCall = createAction(POST_PUNCH_IN_RECORD_CALL);
export const postPunchInRecordDone = createAction(POST_PUNCH_IN_RECORD_DONE);
export const postPunchInRecordFail = createAction(POST_PUNCH_IN_RECORD_FAIL);
export const postPunchInRecordQueueCall = createAction(POST_PUNCH_IN_RECORD_QUEUE_CALL);
export const postPunchInRecordQueueDone = createAction(POST_PUNCH_IN_RECORD_QUEUE_DONE);
export const postPunchInRecordQueueFail = createAction(POST_PUNCH_IN_RECORD_QUEUE_FAIL);
export const getDayShiftsCall = createAction(GET_DAY_SHIFTS_CALL);
export const getDayShiftsDone = createAction(GET_DAY_SHIFTS_DONE);
export const getDayShiftsFail = createAction(GET_DAY_SHIFTS_FAIL);
export const getAllShiftsCall = createAction(GET_ALL_SHIFTS_CALL);
export const getAllShiftsDone = createAction(GET_ALL_SHIFTS_DONE);
export const getAllShiftsFail = createAction(GET_ALL_SHIFTS_FAIL);
export const getSuitableShiftsCall = createAction(GET_SUITABLE_SHIFTS_CALL);
export const getSuitableShiftsDone = createAction(GET_SUITABLE_SHIFTS_DONE);
export const getSuitableShiftsFail = createAction(GET_SUITABLE_SHIFTS_FAIL);
export const getScheduleShiftsCall = createAction(GET_SCHEDULE_SHIFTS_CALL);
export const getScheduleShiftsDone = createAction(GET_SCHEDULE_SHIFTS_DONE);
export const getScheduleShiftsFail = createAction(GET_SCHEDULE_SHIFTS_FAIL);
export const getDailyShiftsCall = createAction(GET_DAILY_SHIFTS_CALL);
export const getDailyShiftsDone = createAction(GET_DAILY_SHIFTS_DONE);
export const getDailyShiftsFail = createAction(GET_DAILY_SHIFTS_FAIL);
export const getRestPunchRecordCall = createAction(GET_REST_PUNCH_RECORD_CALL);
export const getRestPunchRecordDone = createAction(GET_REST_PUNCH_RECORD_DONE);
export const getRestPunchRecordFail = createAction(GET_REST_PUNCH_RECORD_FAIL);
export const clearRestPunchRecord = createAction(CLEAR_REST_PUNCH_RECORD);
export const postRestPunchRecordCall = createAction(POST_REST_PUNCH_RECORD_CALL);
export const postRestPunchRecordDone = createAction(POST_REST_PUNCH_RECORD_DONE);
export const postRestPunchRecordFail = createAction(POST_REST_PUNCH_RECORD_FAIL);
export const deleteRestPunchRecordCall = createAction(DELETE_REST_PUNCH_RECORD_CALL);
export const deleteRestPunchRecordDone = createAction(DELETE_REST_PUNCH_RECORD_DONE);
export const deleteRestPunchRecordFail = createAction(DELETE_REST_PUNCH_RECORD_FAIL);
export const getPunchInRuleCall = createAction(GET_PUNCH_IN_RULE_CALL);
export const getPunchInRuleDone = createAction(GET_PUNCH_IN_RULE_DONE);
export const getPunchInRuleFail = createAction(GET_PUNCH_IN_RULE_FAIL);
export const getPunchInShiftCall = createAction(GET_PUNCH_IN_SHIFT_CALL);
export const getPunchInShiftDone = createAction(GET_PUNCH_IN_SHIFT_DONE);
export const getPunchInShiftFail = createAction(GET_PUNCH_IN_SHIFT_FAIL);

export const getPunchInShiftsByRangeCall = createAction(GET_PUNCH_IN_SHIFTS_BY_RANGE_CALL);
export const getPunchInShiftsByRangeDone = createAction(GET_PUNCH_IN_SHIFTS_BY_RANGE_DONE);
export const getPunchInShiftsByRangeFail = createAction(GET_PUNCH_IN_SHIFTS_BY_RANGE_FAIL);
export const setPunchInStale = createAction<boolean>(SET_PUNCH_IN_STALE);
export const pushPunchInRetryOutcome = createAction<PunchInRetryOutcome>(PUSH_PUNCH_IN_RETRY_OUTCOME);
export const clearPunchInRetryOutcome = createAction<string>(CLEAR_PUNCH_IN_RETRY_OUTCOME);
export const setAsyncPunchErrorModal = createAction<AsyncPunchError>(SET_ASYNC_PUNCH_ERROR_MODAL);
// 帶 record id 做 ownership guard；A 的遲到 callback 不可清掉後來排到畫面上的 B。
export const clearAsyncPunchErrorModal = createAction<string>(CLEAR_ASYNC_PUNCH_ERROR_MODAL);
export const setAsyncPunchInteractionLock = createAction<AsyncPunchInteractionLock>(SET_ASYNC_PUNCH_INTERACTION_LOCK);
// payload 帶 ownerId，避免同 shift/duty 的舊 timeout / 舊 request 收尾誤清後來的新鎖。
export const clearAsyncPunchInteractionLock = createAction<string | undefined>(CLEAR_ASYNC_PUNCH_INTERACTION_LOCK);
export const setMachinePunchError =
	createAction<{ accountId: string; errorType: string } | null>(SET_MACHINE_PUNCH_ERROR);
